import * as React from 'react';
import { cn } from '@/lib/utils/cn';
import type { Unit, UnitTone } from '@/components/ui/charts/unit-squares';

/**
 * RatingChip — a vendor intelligence rating (T-0267; E14-design §8;
 * ADR-025 decisions 3 and 4): Strong, Partial, Gap or Unknown for one
 * product in one library module.
 *
 * Two states, so a reader sees at a glance which ratings count:
 *   - proposed: outlined in the rating's tone, no fill. Code or the AI
 *     proposed it; it enters no number until a consultant confirms it;
 *   - confirmed: filled with the tone's tint, as the posture chips are.
 *
 * Tones come from the posture tokens (healthy, at risk, critical,
 * unassessed) through the Tailwind theme; nothing here picks a colour.
 *
 * Load-bearing primitive (CLAUDE.md rule 3): the ratings grid, the rating
 * panel, and later the report editor all use this one chip.
 */

export const RATING_VALUES = ['strong', 'partial', 'gap', 'unknown'] as const;
export type RatingValue = (typeof RATING_VALUES)[number];
export type RatingState = 'proposed' | 'confirmed';

export const RATING_LABEL: Record<RatingValue, string> = {
  strong: 'Strong',
  partial: 'Partial',
  gap: 'Gap',
  unknown: 'Unknown',
};

const FILLED: Record<RatingValue, string> = {
  strong: 'border-transparent bg-posture-healthy-tint text-posture-healthy',
  partial: 'border-transparent bg-posture-at-risk-tint text-posture-at-risk',
  gap: 'border-transparent bg-posture-critical-tint text-posture-critical',
  unknown: 'border-transparent bg-ink-100 text-posture-unassessed',
};

const OUTLINED: Record<RatingValue, string> = {
  strong: 'border-posture-healthy bg-transparent text-posture-healthy',
  partial: 'border-posture-at-risk bg-transparent text-posture-at-risk',
  gap: 'border-posture-critical bg-transparent text-posture-critical',
  unknown: 'border-posture-unassessed bg-transparent text-posture-unassessed',
};

export interface RatingChipProps {
  rating: RatingValue;
  state: RatingState;
  size?: 'sm' | 'md';
  className?: string;
}

export function RatingChip({ rating, state, size = 'md', className }: RatingChipProps) {
  const label = RATING_LABEL[rating];
  const stateWord = state === 'proposed' ? 'proposed' : 'confirmed';
  return (
    <span
      title={`${label}, ${stateWord}`}
      data-rating={rating}
      data-state={state}
      className={cn(
        // `relative` keeps the screen-reader text inside the chip: positioned
        // against an ancestor outside a scrolling grid, it would widen the page.
        'relative inline-flex flex-none items-center whitespace-nowrap rounded-full border font-medium',
        size === 'sm'
          ? 'h-5 px-2 text-[11px] leading-4'
          : 'h-[22px] px-2.5 text-meta leading-[18px]',
        state === 'confirmed' ? FILLED[rating] : OUTLINED[rating],
        className,
      )}
    >
      {label}
      <span className="sr-only">{`, ${stateWord}`}</span>
    </span>
  );
}

const UNIT_TONE: Record<RatingValue, UnitTone> = {
  strong: 'healthy',
  partial: 'at-risk',
  gap: 'critical',
  unknown: 'unassessed',
};

const UNIT_GLYPH: Record<RatingValue, string> = {
  strong: 'S',
  partial: 'P',
  gap: 'G',
  unknown: '?',
};

/**
 * A confirmed rating as one `UnitSquares` square (the engagement Overview's
 * vendor fit row): the chip's tone filled, the rating's initial inside.
 * `null` is a cell with no confirmed rating yet: a dashed square.
 */
export function ratingUnit(key: string, rating: RatingValue | null, title?: string): Unit {
  return rating === null
    ? { key, tone: 'pending', ...(title ? { title } : {}) }
    : { key, tone: UNIT_TONE[rating], glyph: UNIT_GLYPH[rating], ...(title ? { title } : {}) };
}

/** The legend swatch tone of each rating, and of "not confirmed yet". */
export const RATING_UNIT_TONE: Record<RatingValue | 'pending', UnitTone> = {
  ...UNIT_TONE,
  pending: 'pending',
};
